/**
 * Reusable guided walkthrough built on react-joyride.
 * - Auto-fires once per device (localStorage seen-gate), replayable via a
 *   window event `tour:replay:<tourId>`.
 * - Adaptive: drops any step whose target selector isn't in the DOM, so an
 *   empty-state page never points at a missing widget.
 * - Brand-matched custom tooltip (help-family inline palette); color-blind-safe
 *   ("Step N of M" text progress, icon+label buttons, position-based spotlight).
 */
import React, { useEffect, useState } from 'react';
import { Joyride, STATUS } from 'react-joyride';

const SEEN_PREFIX = 'foundations_tour_';

export function tourSeenKey(tourId) {
  return `${SEEN_PREFIX}${tourId}_v1`;
}
export function getTourSeen(tourId) {
  try {
    return localStorage.getItem(tourSeenKey(tourId)) === '1';
  } catch {
    return false;
  }
}
export function setTourSeen(tourId) {
  try {
    localStorage.setItem(tourSeenKey(tourId), '1');
  } catch {
    /* localStorage unavailable (private mode) — tour just re-shows next visit */
  }
}
/** Keep only steps whose target is 'body'/centered or resolves to a DOM node now. */
export function filterPresentSteps(steps) {
  return steps.filter((s) => {
    if (!s.target || s.target === 'body') return true;
    try {
      return !!document.querySelector(s.target);
    } catch {
      return false;
    }
  });
}

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

const ghostBtn = {
  background: 'transparent',
  border: '1px solid rgba(255,255,255,0.18)',
  color: '#ccd6f6',
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: '0.8rem',
  fontWeight: 600,
  fontFamily: 'inherit',
  cursor: 'pointer',
};
const primaryBtn = {
  background: '#60a5fa',
  border: '1px solid #60a5fa',
  color: '#0a1929',
  borderRadius: 8,
  padding: '6px 14px',
  fontSize: '0.8rem',
  fontWeight: 700,
  fontFamily: 'inherit',
  cursor: 'pointer',
};

function TourTooltip({
  index,
  size,
  step,
  backProps,
  primaryProps,
  skipProps,
  tooltipProps,
  isLastStep,
}) {
  return (
    <div
      {...tooltipProps}
      style={{
        background: '#0f2236',
        border: '1px solid rgba(96,165,250,0.3)',
        borderRadius: 12,
        padding: 20,
        maxWidth: 360,
        color: '#e6f1ff',
        fontFamily: 'inherit',
        boxShadow: '0 12px 40px rgba(0,0,0,0.5)',
      }}
    >
      {step.title && (
        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: 8, color: '#e6f1ff' }}>
          {step.title}
        </div>
      )}
      <div style={{ fontSize: '0.875rem', lineHeight: 1.55, color: '#a8b2d1' }}>{step.content}</div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 16,
          gap: 12,
        }}
      >
        <span style={{ fontSize: '0.75rem', color: '#7a8599' }}>
          Step {index + 1} of {size}
        </span>
        <div style={{ display: 'flex', gap: 8 }}>
          {!isLastStep && (
            <button {...skipProps} style={ghostBtn}>
              Skip
            </button>
          )}
          {index > 0 && (
            <button {...backProps} style={ghostBtn}>
              Back
            </button>
          )}
          <button {...primaryProps} style={primaryBtn}>
            {isLastStep ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GuidedTour({ tourId, steps, autoStart = true }) {
  const [run, setRun] = useState(false);
  const [activeSteps, setActiveSteps] = useState([]);

  // Compute present steps AFTER commit (siblings are in the DOM by now) and
  // auto-start once if unseen.
  useEffect(() => {
    const present = filterPresentSteps(steps);
    setActiveSteps(present);
    if (autoStart && present.length > 0 && !getTourSeen(tourId)) {
      setRun(true);
    }
  }, [tourId, autoStart, steps]);

  // Replay hook: any "Take a tour" button dispatches window event
  // `tour:replay:<tourId>`.
  useEffect(() => {
    const handler = () => {
      setActiveSteps(filterPresentSteps(steps));
      setRun(true);
    };
    const evt = `tour:replay:${tourId}`;
    window.addEventListener(evt, handler);
    return () => window.removeEventListener(evt, handler);
  }, [tourId, steps]);

  const handleCallback = (data) => {
    if (data.status === STATUS.FINISHED || data.status === STATUS.SKIPPED) {
      setTourSeen(tourId);
      setRun(false);
    }
  };

  if (activeSteps.length === 0) return null;

  return (
    <Joyride
      steps={activeSteps}
      run={run}
      continuous
      showSkipButton
      showProgress={false}
      scrollToFirstStep
      disableOverlayClose
      spotlightPadding={6}
      tooltipComponent={TourTooltip}
      callback={handleCallback}
      disableAnimation={prefersReducedMotion()}
      styles={{
        options: {
          zIndex: 10000,
          overlayColor: 'rgba(2,8,20,0.7)',
          primaryColor: '#60a5fa',
          arrowColor: '#0f2236',
        },
      }}
    />
  );
}
